import { Link } from '@inertiajs/react';
import { ReactNode, useState } from 'react';
import { Download, ExternalLink, File, Repeat } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { assetUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { DonationFrequency, Pagination } from '../../../Shared/types';
import { AButton, Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { ColorBadge } from '../../../Shared/UI/ColorBadge';
import { DateFilterInput } from '../../../Shared/UI/DateFilterInput';
import { Icon } from '../../../Shared/UI/Icon';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { Pager } from '../../../Shared/UI/Pager';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { SearchFilterInput } from '../../../Shared/UI/SearchFilterInput';
import { useRouter } from '../../router';
import { DonationForm, SharedProps, Subscription } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { DonationsLayout } from '../DonationsLayout';
import AddSubscriptionForm from './AddSubscriptionForm';
import SubscriptionStatusLabel from './SubscriptionStatusLabel';

export interface SubscriptionsPageProps extends SharedProps {
  subscriptions: Pagination<Subscription & {
    donation_form: DonationForm;
  }>;
  donationForms: PickerItem[];
  exportUrl: string;
}

export default function SubscriptionsPage({
  subscriptions,
  donationForms,
  charity,
  exportUrl,
  params,
}: SubscriptionsPageProps) {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, formatNumber } = useLocale();
  const { routes } = useRouter();

  const showFilters = !!params.search || !!params.donation_form_id || !!params.created_after || !!params.created_until || subscriptions.meta.total > 0;
  const containerSize = showFilters ? 'lg' : 'md';

  const [showImportSubscriptionModal, setShowImportSubscriptionModal] = useState(false);
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(!!params.add_subscription);

  const [showExportModal, setShowExportModal] = useState(false);

  return (
    <>
      <div>
        <Header>
          <Container size={containerSize}>
            <div className="flex items-center justify-between">
              <HeaderTitle>
                {t('backend:recurring_donations')}
              </HeaderTitle>
              <div className="space-x-2">
                <Button onClick={() => setShowImportSubscriptionModal(true)} variant="secondary">
                  {t('backend:import')}
                </Button>
                {charity.has_subscriptions && (
                  <Button onClick={() => setShowAddSubscriptionModal(true)}>
                    {t('backend:add_recurring_donation')}
                  </Button>
                )}
              </div>
            </div>
          </Container>
        </Header>

        <Container size={containerSize} className="space-y-6">
          {showFilters && (
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-3 space-y-6">
                <div className="space-y-4">
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="search">
                      {t('shared:filter.search')}
                    </InputLabel>
                    <SearchFilterInput placeholder="" id="search" />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="donation_form">
                      {t('backend:donation_form')}
                    </InputLabel>
                    <Picker
                      name="donation_form_id"
                      items={donationForms}
                      selected={typeof params.donation_form_id === 'string' ? params.donation_form_id : null}
                      icon={<File />}
                      optional
                      payload={() => ({ page: undefined })}
                      id="donation_form"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="frequency">
                      {t('backend:frequency')}
                    </InputLabel>
                    <Picker
                      name="frequency"
                      items={[
                        {
                          id: DonationFrequency.Monthly,
                          title: t('backend:monthly'),
                        },
                        {
                          id: DonationFrequency.Yearly,
                          title: t('backend:yearly'),
                        },
                      ]}
                      selected={typeof params.frequency === 'string' ? params.frequency : null}
                      icon={<Repeat />}
                      optional
                      payload={() => ({ page: undefined })}
                      id="frequency"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="created_after">
                      {t('backend:from_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_after"
                      selectsStart
                      endDate={typeof params.created_until === 'string' ? params.created_until : undefined}
                      id="created_after"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel className="text-sm font-medium text-slate-500" htmlFor="created_until">
                      {t('backend:until_date')}
                    </InputLabel>
                    <DateFilterInput
                      name="created_until"
                      selectsEnd
                      startDate={typeof params.created_after === 'string' ? params.created_after : undefined}
                      id="created_until"
                    />
                  </InputGroup>
                </div>
                <div>
                  <Button
                    onClick={() => setShowExportModal(true)}
                    variant="secondary"
                    size="sm"
                    disabled={subscriptions.meta.total === 0 || subscriptions.meta.total > 3000}
                  >
                    <Icon className="mr-1">
                      <Download />
                    </Icon>
                    {t('backend:export_selection')}
                    {' '}
                    ({formatNumber(subscriptions.meta.total)})
                  </Button>
                </div>
              </div>
              <div className="col-span-9 space-y-6">
                {subscriptions.data.length > 0 && (
                  <div className="space-y-2">
                    {subscriptions.data.map((subscription) => (
                      <Card
                        key={subscription.id}
                        spacing="xs"
                        link={{
                          href: routes.subscription_page(subscription.id),
                          ariaLabel: subscription.company_name || subscription.full_name || subscription.email || t('backend:anonymous'),
                        }}
                      >
                        <div className="grid grid-cols-[32px_4fr_4fr_3fr_2fr_2fr] gap-4 items-center leading-tight">
                          <div>
                            <ColorBadge
                              color={charity.brand_color}
                              name={subscription.company_name || subscription.full_name || subscription.email || t('backend:anonymous')}
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="overflow-hidden leading-tight whitespace-nowrap text-ellipsis">
                              {subscription.company_name || subscription.full_name || subscription.email || t('backend:anonymous')}
                            </h4>
                          </div>
                          <div className="min-w-0 pr-6 text-sm">
                            <div className="overflow-hidden whitespace-nowrap text-ellipsis">
                              <Icon className="mr-1">
                                <File />
                              </Icon>
                              {subscription.donation_form.internal_title}
                            </div>
                          </div>
                          <div className="text-sm">
                            <span className="font-medium">
                              {formatCurrency(subscription.amount)}
                            </span>
                            <span className="text-slate-400 text-xs">
                              {subscription.frequency === DonationFrequency.Monthly && t('backend:per_month_abbr')}
                              {subscription.frequency === DonationFrequency.Yearly && t('backend:per_year_abbr')}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500">
                            {formatDate(subscription.start_date)}
                          </div>
                          <div className="text-sm text-right">
                            <SubscriptionStatusLabel active={subscription.active} />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}

                {subscriptions.meta.last_page > 1 && (
                  <Pager data={subscriptions} />
                )}

                {showFilters && subscriptions.meta.total === 0 && (
                  <Card className="py-24 text-center text-slate-500">
                    <img
                      src={assetUrl('images/no-results.svg')}
                      alt={t('backend:no_results')}
                      width={120}
                      className="block mx-auto mb-6"
                    />
                    {t('backend:no_results')}
                  </Card>
                )}
              </div>
            </div>
          )}

          {!showFilters && (
            <Card className="text-center text-slate-500">
              <h2>
                {t('backend:no_recurring_donations_yet')}
              </h2>
              <Trans i18nKey="backend:no_recurring_donations_yet_description">
                <Link href={routes.donation_forms_page()} className="font-medium" />
              </Trans>
            </Card>
          )}
        </Container>
      </div>

      <Modal isOpen={showImportSubscriptionModal} onClose={() => setShowImportSubscriptionModal(false)} size="sm">
        <ImportDonationsInfo />
      </Modal>

      <Modal isOpen={showAddSubscriptionModal} onClose={() => setShowAddSubscriptionModal(false)} size="sm">
        <AddSubscriptionForm donationForms={donationForms} charity={charity} />
      </Modal>

      <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} size="sm">
        <ExportSubscriptionsModal count={subscriptions.meta.total} exportUrl={exportUrl} onExport={() => setShowExportModal(false)} />
      </Modal>
    </>
  );
}

SubscriptionsPage.layout = (page: ReactNode) => <Layout><DonationsLayout>{page}</DonationsLayout></Layout>;

const ImportDonationsInfo = () => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <div className="space-y-6">
      <h3 className="py-1">
        {t('backend:import_recurring_donations')}
      </h3>
      <hr />
      <div className="space-y-3">
        <p>
          {t('backend:import_recurring_donations_description_1')}
        </p>
        <p>
          {t('backend:import_recurring_donations_description_2')}
        </p>
        <p>
          <AButton
            href={`https://about.supporta.com/${locale}/contact`}
            variant="secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('backend:contact_us')}
            <Icon className="ml-1">
              <ExternalLink />
            </Icon>
          </AButton>
        </p>
      </div>
    </div>
  );
};

const ExportSubscriptionsModal = ({
  count,
  exportUrl,
  onExport,
}: {
  count: number;
  exportUrl: string;
  onExport: () => void;
}) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const [splitLastNames, setSplitLastNames] = useState(false);

  const getExportUrl = () => {
    const url = new URL(exportUrl);

    if (splitLastNames) {
      url.searchParams.append('split_last_names', '1');
    }

    return url.toString();
  };

  return (
    <div className="space-y-6">
      <h3 className="py-1">
        {t('backend:export_recurring_donations', { total: formatNumber(count) })}
      </h3>

      <hr />

      <InputGroup>
        <CheckboxLabel htmlFor="export_split_last_names">
          <Checkbox
            onChange={() => setSplitLastNames((split) => !split)}
            checked={splitLastNames}
            className="mt-1"
            id="export_split_last_names"
          />
          <span>
            {t('backend:split_last_names_label')}
          </span>
        </CheckboxLabel>
      </InputGroup>

      <hr />

      <div className="text-right">
        <AButton
          href={getExportUrl()}
          onClick={() => onExport()}
        >
          {t('backend:export')}
        </AButton>
      </div>
    </div>
  );
};
